import React, { useState, useContext, useEffect, useRef } from 'react';
import { similarity } from '../utils/stringSimilarity';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { useDialog } from '../components/ConfirmProvider';
import { FaSearch, FaTimes, FaInfoCircle, FaUpload, FaHome, FaGlobe, FaUniversity } from 'react-icons/fa';
import ActionDropdown from './ActionDropdown';
import '../Pages.css';
import './Pips.css';
import PersonalInfoForm from '../DataCapturer/forms/PersonalInfoForm';
import ForeignDetailsForm from '../DataCapturer/forms/ForeignDetailsForm';
import InstitutionsForm from '../DataCapturer/forms/InstitutionsForm';
import AssociatesForm from '../DataCapturer/forms/AssociatesForm';
import { countries } from '../DataCapturer/constants';
import BulkSearchForm from '../DataCapturer/forms/BulkSearchForm';
import EditPipModal from './EditPipModal';
import { exportBrandedPdf, PDF_COLOURS } from '../utils/brandedPdf';
import Pagination from '../components/Pagination';
import { formatDate } from '../utils/dateFormat';

// dd/mm/yyyy with HH:MM time. Returns '—' for falsy input.
const formatLastUpdated = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy}, ${hh}:${mi}`;
};

// Alert Banner Component
const AlertBanner = ({ type, message, onClose, actions }) => {
  const iconMap = {
    warning: '⚠️',
    danger: '🚫',
    info: 'ℹ️',
    success: '✅'
  };

  return (
    <div className={`alert-banner alert-banner--${type}`}>
      <div className="alert-banner__body">
        <span className="alert-banner__icon">{iconMap[type]}</span>
        <span>{message}</span>
      </div>
      <div className="alert-banner__actions">
        {actions && actions.map((action, idx) => (
          <button
            key={idx}
            onClick={action.onClick}
            className={`alert-banner__action-btn${action.primary ? ' alert-banner__action-btn--primary' : ''}`}
          >
            {action.label}
          </button>
        ))}
        {onClose && (
          <button onClick={onClose} className="alert-banner__close">×</button>
        )}
      </div>
    </div>
  );
};

function PIPs() {
  const { user, setUser } = useContext(UserContext);
  const { confirm, notify } = useDialog();
  const [allPips, setAllPips] = useState([]);
  const [selectedPipIds, setSelectedPipIds] = useState([]);
  // Handle select all on current page
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPipIds(currentPips.map(p => p.id));
    } else {
      setSelectedPipIds([]);
    }
  };

  // Handle select one
  const handleSelectOne = (id, checked) => {
    setSelectedPipIds(prev => checked ? [...prev, id] : prev.filter(pid => pid !== id));
  };
  const [loading, setLoading] = useState(false);
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('full_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLimitInfo, setSearchLimitInfo] = useState(null);
  const [pipsLastUpdated, setPipsLastUpdated] = useState(null);
  const [pipStats, setPipStats] = useState(null);
  const [, setDashboardLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchHistory, setSearchHistory] = useState([]);
  const [isPrivilegedUser, setIsPrivilegedUser] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPip, setEditingPip] = useState(null);
  const [editCurrentStep, setEditCurrentStep] = useState(0);
  const [editCompletedSteps, setEditCompletedSteps] = useState([]);
  const [editValidationErrors, setEditValidationErrors] = useState({});
  const [savingStep, setSavingStep] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showBulkSearchModal, setShowBulkSearchModal] = useState(false); // State to control modal visibility
  const [bulkSearchInfo, setBulkSearchInfo] = useState(null); // Add state for bulk search info
  const [, setUnmatchedBulkInputs] = useState([]); // Track unmatched bulk search keywords for future use
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [fullQueryToShow, setFullQueryToShow] = useState('');
  // Table search state
  const [tableSearch, setTableSearch] = useState('');
  // Client-side chip filter: 'all' | 'domestic' | 'foreign' | 'international'
  const [chipFilter, setChipFilter] = useState('all');
  // Approximity (similarity) slider state
  const [minSimilarity, setMinSimilarity] = useState(0);
  const searchInputRef = useRef(null);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  // The PIP whose details are open in the View Details modal
  const [detailPip, setDetailPip] = useState(null);

  // Esc closes the View Details modal
  useEffect(() => {
    if (!detailPip) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDetailPip(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detailPip]);

  // Edit form states
  const [editFirstName, setEditFirstName] = useState('');
  const [editMiddleName, setEditMiddleName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editNationalId, setEditNationalId] = useState('');
  const [editAssociates, setEditAssociates] = useState([]);
  const [editInstitutions, setEditInstitutions] = useState([]);
  const [editForeignDetails, setEditForeignDetails] = useState({ country: '', additional_notes: '' });
  const [editSuggestions, setEditSuggestions] = useState([]);
  const [editPipResultOverview, setEditPipResultOverview] = useState('');
  const [viewMode, setViewMode] = useState(false); // Add this state

  // Dashboard card styles now use CSS classes: stat-card, stat-label, stat-value

  // Helper function to format search history display
  const formatSearchHistoryDisplay = (search) => {
    // Check if the search is a JSON array (bulk search)
    if (search.startsWith('[{') && search.includes('first_name')) {
      try {
        const parsed = JSON.parse(search);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const firstPerson = parsed[0];
          const firstName = firstPerson.first_name || '';
          const lastName = firstPerson.last_name || '';
          const count = parsed.length;
          
          return {
            display: `Bulk: ${firstName} ${lastName}${count > 1 ? ` +${count - 1} more` : ''}`,
            fullQuery: search,
            isBulk: true,
            recordCount: count
          };
        }
      } catch (e) {
        // If parsing fails, treat as regular bulk search
        return {
          display: 'Bulk Search (Multiple Records)',
          fullQuery: search,
          isBulk: true,
          recordCount: 0
        };
      }
    }
    
    // Regular search query
    return {
      display: search.length > 50 ? search.substring(0, 50) + '...' : search,
      fullQuery: search,
      isBulk: false,
      recordCount: 0
    };
  };

  // Add this helper function after the state declarations (around line 50)
  const getPipsLength = () => Array.isArray(allPips) ? allPips.length : 0;
  const getPipsArray = () => Array.isArray(allPips) ? allPips : [];

  useEffect(() => {
    // Try to refresh the user data from localStorage
    const storedUser = localStorage.getItem("user");
    if (storedUser && (!user || !user.first_name || !user.organisation_name)) {
      try {
        const parsedUser = JSON.parse(storedUser);
      } catch (err) {
        console.error("Error parsing stored user:", err);
      }
    }

    // Permission-based: system admins, or users with PIP-management permissions
    // (anyone who can create/update/import PIPs) get the full list view.
    if (user) {
      const perms = Array.isArray(user.permissions) ? user.permissions : [];
      const hasPrivilegedAccess = user.is_system_admin === true ||
        perms.includes('pips.create') ||
        perms.includes('pips.update') ||
        perms.includes('pips.import');

      setIsPrivilegedUser(hasPrivilegedAccess);

      // Load all PIPs automatically for privileged users
      if (hasPrivilegedAccess) {
        loadAllPips();
      }
    }

    if (user?.organisation_id) {
      fetchDashboardInfo();
      fetchRecentSearches();
    }
    
    // Focus the search input on component mount
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [user]);

  // Header stat cards — visible to all roles. Refresh after search/import
  // so counts and "last updated" stay current.
  const fetchPipStats = async () => {
    try {
      const res = await axios.get('/pips/stats');
      if (res.data) setPipStats(res.data);
    } catch (e) {
      console.error('Error fetching PIP stats:', e.message);
    }
  };
  useEffect(() => {
    if (user) fetchPipStats();
  }, [user]);

  // Update the useEffect to load search limit info on mount (around line 170)

  // Add this useEffect to load search limit info when component mounts
  useEffect(() => {
    if (!isPrivilegedUser) {
      // Fetch initial search limit info even without searching
      const fetchSearchLimitInfo = async () => {
        try {
          const response = await axios.get('/pips');
          if (response.meta?.searchLimitInfo) {
            setSearchLimitInfo(response.meta.searchLimitInfo);
          }
        } catch (error) {
          console.error('Error fetching search limit info:', error);
        }
      };
      
      fetchSearchLimitInfo();
    }
  }, [isPrivilegedUser]);

  // Function to load all PIPs for privileged users
  const loadAllPips = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get('/pips');

      // Handle the response structure properly
      if (Array.isArray(res.data)) {
        setAllPips(res.data);
      } else {
        setAllPips([]);
      }

      // Update search limit info if provided
      if (res.meta?.searchLimitInfo) {
        setSearchLimitInfo(res.meta.searchLimitInfo);
      }
      if (res.meta?.lastUpdated !== undefined) {
        setPipsLastUpdated(res.meta.lastUpdated);
      }

      setSearchSubmitted(true);
    } catch (err) {
      console.error('Error loading PIPs:', err);
      setError(err.message || 'Failed to load PIPs');
      setAllPips([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch recent searches for suggestions
  const fetchRecentSearches = async () => {
    try {
      const res = await axios.get('/audit/pip-searches');
      if (res.data && Array.isArray(res.data)) {
        // Extract unique search queries from history, filter out any containing 'bulk search' (case-insensitive)
        const searches = res.data
          .map(item => item.search_query)
          .filter((value, index, self) => {
            if (!value) return false;
            // Remove if contains 'bulk search' (case-insensitive)
            if (typeof value === 'string' && value.toLowerCase().includes('bulk search')) return false;
            return self.indexOf(value) === index;
          })
          .slice(0, 5); // Get top 5 recent searches
        setSearchHistory(searches);
      }
    } catch (err) {
      console.error("Error fetching search history:", err);
    }
  };

  const fetchDashboardInfo = async () => {
    if (!user?.organisation_id) return;
    
    try {
      setDashboardLoading(true);
      const res = await axios.get(`/audit/pip-searches/dashboard?organisation_id=${user.organisation_id}`);
      if (res.data) {
        setSearchLimitInfo({
          package: res.data.package,
          screeningLimit: res.data.screening_limit,
          screeningsDone: res.data.screenings_done,
          screeningsLeft: res.data.screenings_left,
          batchScreeningLimit: res.data.batch_screening_limit,
          batchScreeningsDone: res.data.batch_screenings_done,
          batchScreeningsLeft: res.data.batch_screenings_left
        });
      }
    } catch (err) {
      console.error("Error fetching dashboard info:", err);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Function to refresh user details from the server
  const refreshUserDetails = async () => {
    try {
      const res = await axios.get('/users/me');
      if (res.data && res.data.user) {
        // Update localStorage with fresh user data
        const freshUserData = res.data.user;
        localStorage.setItem('user', JSON.stringify(freshUserData));
        
        // Also store email separately
        if (freshUserData.email) {
          localStorage.setItem('userEmail', freshUserData.email);
        }
        
        console.log("Refreshed user details:", freshUserData);
        
        // Update context with fresh data
        if (setUser) {
          setUser(freshUserData);
        }
        
        // Return the refreshed user data
        return freshUserData;
      }
    } catch (err) {
      console.error("Error refreshing user details:", err);
    }
    return null;
  };

  const getAssociateName = (assoc) =>
    [assoc.first_name, assoc.middle_name, assoc.last_name].filter(Boolean).join(' ');

  const getPipResultOverview = (pip) => {
    if (!pip) return 'N/A';
    const candidates = [
      pip.pip_result_overview,
      pip.result_overview,
      pip.reason,
      pip.pip_reason,
      pip.overview
    ];

    for (const candidate of candidates) {
      if (candidate == null) continue;
      const text = String(candidate).trim();
      if (text) {
        return text;
      }
    }

    return 'N/A';
  };

  const handleSearch = async () => {
    if (!searchTerm.trim() && !isPrivilegedUser) return;
    
    // Check if search limit is reached first (only for non-privileged users)
    if (!isPrivilegedUser && searchLimitInfo) {
      const hasLimit = searchLimitInfo.screeningLimit !== null && searchLimitInfo.screeningLimit !== 'Unlimited';
      const remaining = searchLimitInfo.screeningsLeft;
      
      // If limit is reached, show error and return
      if (hasLimit && remaining === 0) {
  setError(`Your organisation has reached the maximum number of searches (${searchLimitInfo.screeningLimit}) allowed by your package. Please contact your administrator to upgrade your package.`);
        return;
      }
      
      // Show warning for low remaining searches
      if (hasLimit && remaining > 0 && remaining <= 5) {
        if (remaining <= 2) {
          const ok = await confirm({
            title: 'Search limit warning',
            message: `You have only ${remaining} search${remaining === 1 ? '' : 'es'} remaining.\n\nDo you want to continue with this search?`,
            confirmLabel: 'Continue',
            cancelLabel: 'Cancel'
          });
          if (!ok) return;
        }
      }
    }
    
    setSearchSubmitted(true);
    try {
      setLoading(true);
      setError(null);
      
      // Build query with active filter if not "all"
      let queryParams = searchTerm.trim() ? `query=${encodeURIComponent(searchTerm.trim())}` : '';
      if (activeFilter !== 'all') {
        queryParams += queryParams ? '&' : '';
        queryParams += `filter=${encodeURIComponent(activeFilter)}`;
      }
      
      const res = await axios.get(`/pips${queryParams ? '?' + queryParams : ''}`);

      // Update all PIPs and search limit info
      if (Array.isArray(res.data)) {
        setAllPips(res.data);
      } else {
        setAllPips([]);
      }

      // Update search limit info if provided
      if (res.meta?.lastUpdated !== undefined) {
        setPipsLastUpdated(res.meta.lastUpdated);
      }
      if (res.meta?.searchLimitInfo) {
        setSearchLimitInfo(res.meta.searchLimitInfo);

        // Show success message with remaining searches
        const remaining = res.meta.searchLimitInfo.screeningsLeft;
        if (!isPrivilegedUser && remaining !== 'Unlimited' && remaining <= 10) {
          setSuccessMessage(`Search successful! You have ${remaining} search${remaining === 1 ? '' : 'es'} remaining.`);
          setTimeout(() => setSuccessMessage(''), 5000);
        }
      }
      
      setCurrentPage(1);
      
      // Refresh dashboard info after successful search
      fetchDashboardInfo();
      
      // Update search history + header stat cards (My searches, last updated)
      fetchRecentSearches();
      fetchPipStats();
      
      // Scroll to results
      setTimeout(() => {
        const resultsElement = document.getElementById('search-results');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      if (err.response?.status === 403) {
        setError(err.message || 'Search limit reached. Please contact your administrator.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Additional helper functions
  const clearSearch = () => {
    setSearchTerm('');
    setAllPips([]);
    setCurrentPage(1);
    setSearchSubmitted(false);
    setActiveFilter('all');
    setBulkSearchInfo(null); // Add this line
    setTableSearch(''); // Reset table search
    setMinSimilarity(0); // Reset search score slider
    setChipFilter('all'); // Reset PIP-type chip filter
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  
  const applyFilter = (filter) => {
    setActiveFilter(filter);
  };
  
  const applySuggestion = (suggestion) => {
    setSearchTerm(suggestion);
    handleSearch();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSort = (col) => {
    const order = sortColumn === col && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortColumn(col);
    setSortOrder(order);
  };



  // Unified similarity filtering for both regular and bulk search results
  const proximityBase = searchTerm.trim();
  let filtered = Array.isArray(allPips)
    ? allPips
        .map(pip => {
          let score = 0;
          // For bulk search with table search keyword, use table search keyword for similarity
          if (bulkSearchInfo && tableSearch.trim()) {
            const t = tableSearch.trim();
            const full = (pip.full_name || '').trim();
            if (t.toLowerCase() === full.toLowerCase() || full.toLowerCase().includes(t.toLowerCase()) || t.toLowerCase().includes(full.toLowerCase())) {
              score = 100;
            } else {
              score = similarity(t, full);
            }
          } else if (bulkSearchInfo && Array.isArray(bulkSearchInfo.inputNames) && bulkSearchInfo.inputNames.length > 0) {
            // For bulk search with no table search, use original input names
            const pipName = (pip.full_name || '').trim();
            let maxScore = 0;
            for (const inputName of bulkSearchInfo.inputNames) {
              const pipNorm = pipName.toLowerCase();
              const inputNorm = inputName.toLowerCase();
              if (pipNorm === inputNorm || pipNorm.includes(inputNorm) || inputNorm.includes(pipNorm)) {
                maxScore = 100;
                break;
              } else {
                const s = similarity(inputName, pipName);
                if (s > maxScore) maxScore = s;
              }
            }
            score = maxScore;
          } else if (!proximityBase) {
            score = 100;
          } else {
            const s = proximityBase.toLowerCase();
            const full = (pip.full_name || '').toLowerCase();
            if (s === full || full.includes(s) || s.includes(full)) {
              score = 100;
            } else {
              score = similarity(proximityBase, pip.full_name || '');
            }
          }
          return { ...pip, _similarityScore: score };
        })
        .filter(pip => pip._similarityScore >= minSimilarity)
    : [];

  // Chip filter — narrows the visible table to the clicked PIP type without re-fetching.
  const chipFiltered = chipFilter === 'all'
    ? filtered
    : filtered.filter(p => {
        const t = (p.pip_type || '').toLowerCase();
        if (chipFilter === 'domestic') return t.includes('domestic') || t === 'local';
        if (chipFilter === 'foreign') return t.includes('foreign');
        if (chipFilter === 'international') return t.includes('international');
        return true;
      });

  const sorted = Array.isArray(chipFiltered) ? [...chipFiltered].sort((a, b) => {
    const valA = (a[sortColumn] || '').toString().toLowerCase();
    const valB = (b[sortColumn] || '').toString().toLowerCase();
    return valA < valB ? (sortOrder === 'asc' ? -1 : 1) : valA > valB ? (sortOrder === 'asc' ? 1 : -1) : 0;
  }) : [];

  const totalPages = Math.ceil((Array.isArray(sorted) ? sorted.length : 0) / itemsPerPage);
  const currentPips = Array.isArray(sorted) ? sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : [];


  const renderPaginationControls = () => (
    <Pagination
      currentPage={currentPage}
      totalItems={sorted.length}
      itemsPerPage={itemsPerPage}
      onPageChange={setCurrentPage}
      onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
    />
  );

  // Function to get user display name
  const getUserDisplayName = () => {
    // Try from user context
    if (user) {
      // Try to build a full name from first_name and last_name
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`;
      }
      
      // Alternative properties
      if (user.name && user.surname) {
        return `${user.name} ${user.surname}`;
      }
      
      // If we only have first_name or name
      if (user.first_name) return user.first_name;
      if (user.name) return user.name;
      
      // Fall back to email if available
      if (user.email) return user.email;
    }
    
    // Try from localStorage as fallback
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser) {
        if (storedUser.first_name && storedUser.last_name) {
          return `${storedUser.first_name} ${storedUser.last_name}`;
        }
        if (storedUser.name && storedUser.surname) {
          return `${storedUser.name} ${storedUser.surname}`;
        }
        if (storedUser.first_name) return storedUser.first_name;
        if (storedUser.name) return storedUser.name;
        if (storedUser.email) return storedUser.email;
      }
    } catch (e) {
      console.error("Error getting user from localStorage:", e);
    }
    
    // Final fallback
    return 'Unknown User';
  };
  
  // Function to get organisation name
  const getOrganizationName = () => {
    // Try from user context
    if (user) {
      if (user.organisation_name) return user.organisation_name;
      if (user.organization_name) return user.organization_name;
    }
    
    // Try from localStorage as fallback
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser) {
        if (storedUser.organisation_name) return storedUser.organisation_name;
        if (storedUser.organization_name) return storedUser.organization_name;
      }
    } catch (e) {
  console.error("Error getting organisation from localStorage:", e);
    }
    
    return 'N/A';
  };


  // PDF export — branded report (utils/brandedPdf)
  const exportPDF = async () => {
    await refreshUserDetails();

    // Only export selected rows, or all if nothing is selected
    const selectedPips = selectedPipIds.length > 0
      ? allPips.filter(p => selectedPipIds.includes(p.id))
      : allPips;

    await exportBrandedPdf({
      title: 'PROMINENT INFLUENTIAL PERSONS — SCREENING REPORT',
      orgName: getOrganizationName(),
      userName: getUserDisplayName(),
      searchLabel: 'Search Term',
      searchValue: (searchTerm ? `"${searchTerm}"` : 'All PIPs') +
        (selectedPipIds.length > 0 ? `  ·  ${selectedPipIds.length} selected` : ''),
      fileName: 'PIPs_Screening_Report',
      columns: [
        { header: 'Full Name', width: 42, bold: true },
        { header: 'Classification', width: 26, align: 'center' },
        { header: 'Institution', width: 44 },
        { header: 'Position', width: 34 },
        { header: 'Country', width: 22, align: 'center' },
        { header: 'PIP Result Overview', width: 56 },
        { header: 'Associates', width: 51 }
      ],
      rows: selectedPips.map(p => [
        p.full_name || '',
        p.pip_type || '',
        Array.isArray(p.institutions) && p.institutions.length > 0
          ? p.institutions.map(i => i.institution_name || '').filter(Boolean).join('; ')
          : 'N/A',
        p.position || 'N/A',
        p.country || 'Namibia',
        getPipResultOverview(p),
        Array.isArray(p.associates) && p.associates.length > 0
          ? p.associates.map(a => {
              const nm = getAssociateName(a) || 'Unnamed';
              const rel = a.relationship_type ? ` (${a.relationship_type})` : '';
              return `${nm}${rel}`;
            }).join('; ')
          : 'None captured'
      ]),
      // Colour-code classification (foreign blue, domestic green)
      cellStyle: (row, col) => {
        if (col !== 1) return undefined;
        const t = (selectedPips[row].pip_type || '').toLowerCase();
        return { fontStyle: 'bold', textColor: t.includes('foreign') ? PDF_COLOURS.blue : PDF_COLOURS.green };
      }
    });
  };



  // Modify handleEdit to accept a viewOnly flag
  const handleEdit = (pip, viewOnly = false) => {
    setEditingPip(pip);
  setEditFirstName(pip.first_name || '');
  setEditMiddleName(pip.middle_name || '');
  setEditLastName(pip.last_name || '');
  setEditNationalId(pip.national_id || '');
    setEditPipResultOverview(pip.reason || pip.pip_result_overview || pip.result_overview || '');
    setEditAssociates(pip.associates?.length > 0 ? pip.associates : 
      [{ first_name: '', middle_name: '', last_name: '', relationship_type: '', national_id: '' }]
    );
    // Normalize institution dates to yyyy-mm-dd for date inputs
    const normalizeDate = (d) => {
      if (!d) return '';
      // If already yyyy-mm-dd
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      const parsed = new Date(d);
      if (isNaN(parsed.getTime())) return '';
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const normalizedInstitutions = (pip.institutions || []).map(inst => ({
      institution_name: inst.institution_name || '',
      institution_type: inst.institution_type || '',
      position: inst.position || '',
      start_date: normalizeDate(inst.start_date || ''),
      end_date: normalizeDate(inst.end_date || '')
    }));
    setEditInstitutions(normalizedInstitutions.length > 0 ? normalizedInstitutions : 
      [{ institution_name: '', institution_type: '', position: '', start_date: '', end_date: '' }]
    );
    const pipTypeStr = (pip?.pip_type || '').toLowerCase();
    const isForeignType = pipTypeStr.includes('foreign');
    if (isForeignType && pip.foreign) {
      setEditForeignDetails({
        country: pip.foreign.country || pip.country || '',
        additional_notes: pip.foreign.additional_notes || ''
      });
    } else {
      setEditForeignDetails({ country: '', additional_notes: '' });
    }
    setEditCurrentStep(0);
    setEditCompletedSteps([]);
    setEditValidationErrors({});
    setViewMode(viewOnly); // Set view mode
    setShowEditModal(true);
  };
  
  // Handle toggle PIP active status
  const handleToggleStatus = async (pipId, newStatus) => {
    try {
      await axios.patch(`/pips/${pipId}`, {
        is_active: newStatus
      });

      // Show success message
      setSuccessMessage(`PIP ${newStatus ? 'activated' : 'deactivated'} successfully`);

      // Refresh PIP list
      if (isPrivilegedUser) {
        loadAllPips();
      } else if (searchSubmitted) {
        handleSearch();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);

      return true;
    } catch (error) {
      setError(error.message || 'Failed to update PIP status');
      console.error('Toggle status error:', error);
      return false;
    }
  };

 

  const validateEditStep = (stepIndex) => {
    const errors = {};

    if (stepIndex === 0) {
      if (!editFirstName.trim()) errors.firstName = 'First name is required';
      if (!editLastName.trim()) errors.lastName = 'Last name is required';
      if (((editingPip?.pip_type || '').toLowerCase().includes('foreign')) && !editNationalId.trim()) {
        errors.nationalId = 'National ID is required for foreign PIPs';
      }
      if (!editPipResultOverview.trim()) {
        errors.pipResultOverview = 'PIP Result Overview is required';
      }
    }

    {
      const pipTypeStr = (editingPip?.pip_type || '').toLowerCase();
      const isForeignType = pipTypeStr.includes('foreign');
      if (isForeignType && stepIndex === 1) {
      if (!editForeignDetails.country.trim()) {
        errors.country = 'Country is required';
      } else if (!countries.includes(editForeignDetails.country)) {
        errors.country = 'Please select a valid country from the list';
      }
      }
    }

    const typeStr = (editingPip?.pip_type || '').toLowerCase();
    const institutionStepIndex = typeStr.includes('foreign') ? 2 : 1;
    if (stepIndex === institutionStepIndex) {
      const hasValidInstitution = editInstitutions.some(inst => inst.institution_name?.trim());
      if (!hasValidInstitution) {
        errors.institutions = 'At least one institution is required';
      }
    }

    setEditValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditStepClick = (index) => {
    // Allow navigation to any step without validation
    setEditCurrentStep(index);
    setEditValidationErrors({});
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingPip(null);
    setEditCurrentStep(0);
    setEditCompletedSteps([]);
    setEditValidationErrors({});
    setEditPipResultOverview('');
  };

  // Add handler functions for edit forms
  const handleEditAssociateChange = (index, field, value) => {
    const updated = [...editAssociates];
    updated[index][field] = value;
    setEditAssociates(updated);
  };

  const addEditAssociate = () => {
    setEditAssociates([...editAssociates, 
      { first_name: '', middle_name: '', last_name: '', relationship_type: '', national_id: '' }
    ]);
  };

  const removeEditAssociate = (index) => {
    if (editAssociates.length > 1) {
      setEditAssociates(editAssociates.filter((_, idx) => idx !== index));
    }
  };

  const handleEditInstitutionChange = (index, field, value) => {
    const updated = [...editInstitutions];
    updated[index][field] = value;
    setEditInstitutions(updated);
    if (field === 'institution_name' && value.trim() && editValidationErrors.institutions) {
      setEditValidationErrors({ ...editValidationErrors, institutions: null });
    }
  };

  const addEditInstitution = () => {
    setEditInstitutions([...editInstitutions, 
      { institution_name: '', institution_type: '', position: '', start_date: '', end_date: '' }
    ]);
  };

  const removeEditInstitution = (index) => {
    if (editInstitutions.length > 1) {
      setEditInstitutions(editInstitutions.filter((_, idx) => idx !== index));
    }
  };

  const handleEditCountryChange = (e) => {
    const val = e.target.value;
    setEditForeignDetails({ ...editForeignDetails, country: val });
    setEditSuggestions(val ? countries.filter(c => c.toLowerCase().startsWith(val.toLowerCase())) : []);
    if (editValidationErrors.country) {
      setEditValidationErrors({ ...editValidationErrors, country: null });
    }
  };

  const selectEditCountry = (country) => {
    setEditForeignDetails({ ...editForeignDetails, country });
    setEditSuggestions([]);
    if (editValidationErrors.country) {
      setEditValidationErrors({ ...editValidationErrors, country: null });
    }
  };

  const handleEditNationalIdChange = (e) => {
    setEditNationalId(e.target.value);
    if (editValidationErrors.nationalId) {
      setEditValidationErrors({ ...editValidationErrors, nationalId: null });
    }
  };

  

  // Add this function after the handleEditStepClick function (around line 600)
  const handleSaveStep = async (targetStep = editCurrentStep) => {
    if (!validateEditStep(targetStep)) {
      return;
    }

    setSavingStep(true);
    setSuccessMessage(''); // Clear previous messages
    try {
      let updateData = {};
      let hasChanges = false;
      
      // Determine what to save based on current step
      if (targetStep === 0) {
        // Save personal information
        updateData = {
          first_name: editFirstName,
          middle_name: editMiddleName,
          last_name: editLastName,
          national_id: editNationalId,
          reason: editPipResultOverview
        };
        // Compare with current editingPip to avoid no-op updates
        const current = editingPip || {};
        hasChanges = (
          (updateData.first_name ?? '') !== (current.first_name ?? '') ||
          (updateData.middle_name ?? '') !== (current.middle_name ?? '') ||
          (updateData.last_name ?? '') !== (current.last_name ?? '') ||
          (updateData.national_id ?? '') !== (current.national_id ?? '') ||
          (updateData.reason ?? '') !== (current.reason ?? '')
        );
      } else if (((editingPip?.pip_type || '').toLowerCase().includes('foreign')) && targetStep === 1) {
        // Save foreign details
        updateData = {
          foreign: editForeignDetails
        };
        const currForeign = editingPip?.foreign || {};
        hasChanges = (
          (updateData.foreign?.country ?? '') !== (currForeign?.country ?? '') ||
          (updateData.foreign?.additional_notes ?? '') !== (currForeign?.additional_notes ?? '')
        );
      } else if ((((editingPip?.pip_type || '').toLowerCase().includes('foreign')) && targetStep === 2) || 
                 (!((editingPip?.pip_type || '').toLowerCase().includes('foreign')) && targetStep === 1)) {
        // Save institutions
        updateData = {
          institutions: editInstitutions.filter(i => i.institution_name?.trim())
        };
        const currInst = (editingPip?.institutions || []).map(i => ({
          institution_name: i.institution_name || '',
          institution_type: i.institution_type || '',
          position: i.position || '',
          start_date: i.start_date || '',
          end_date: i.end_date || ''
        }));
        const newInst = updateData.institutions.map(i => ({
          institution_name: i.institution_name || '',
          institution_type: i.institution_type || '',
          position: i.position || '',
          start_date: i.start_date || '',
          end_date: i.end_date || ''
        }));
        hasChanges = JSON.stringify(currInst) !== JSON.stringify(newInst);
      } else {
        // Save associates
        updateData = {
          associates: editAssociates.filter(a => {
            const fn = a.first_name?.trim();
            const mn = a.middle_name?.trim();
            const ln = a.last_name?.trim();
            const rel = a.relationship_type?.trim();
            const nid = a.national_id?.trim();
            return !!(fn || mn || ln || rel || nid);
          })
        };
        // Normalize and trim for robust comparison (optional logging)
        const normalizeAssoc = (a) => ({
          first_name: (a.first_name || '').trim(),
          middle_name: (a.middle_name || '').trim(),
          last_name: (a.last_name || '').trim(),
          relationship_type: (a.relationship_type || '').trim(),
          national_id: (a.national_id || '').trim()
        });
        const currAssoc = (editingPip?.associates || []).map(normalizeAssoc);
        const newAssoc = updateData.associates.map(normalizeAssoc);
        // Always allow save for associates to avoid false "no change" cases
        hasChanges = true;
      }

      if (!hasChanges) {
        setSuccessMessage('No changes to save.');
        setTimeout(() => setSuccessMessage(''), 2000);
        return;
      }

      console.log('Sending update data:', updateData);
      console.log('To PIP ID:', editingPip.id);
      
      const response = await axios.put(`/pips/${editingPip.id}`, updateData);

      console.log('Update response:', response.data);

      setSuccessMessage('✅ Step saved successfully!');

      // Mark step as completed
      setEditCompletedSteps((prev) => (
        prev.includes(targetStep) ? prev : [...prev, targetStep]
      ));

      // Refresh the PIPs list
      if (isPrivilegedUser) {
        loadAllPips();
      } else {
        handleSearch();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setError('Failed to save changes: ' + error.message);
    } finally {
      setSavingStep(false);
    }
  };

  const handleBulkSearch = () => {
    if (searchLimitInfo?.batchScreeningsLeft === 0 && searchLimitInfo?.batchScreeningLimit !== null) {
  setError('Your organisation has reached the batch screening limit. Please contact your administrator to upgrade your package.');
      return;
    }
    setShowBulkSearchModal(true); // Open the modal
  };

  const closeBulkSearchModal = () => {
    setShowBulkSearchModal(false); // Close the modal
  };

  // Add this function after the closeBulkSearchModal function (around line 820)
  const handleBulkSearchComplete = (pipsData, searchLimitInfo, bulkInfo) => {
    // Ensure pipsData is an array
    const pipsArray = Array.isArray(pipsData) ? pipsData : [];
    setAllPips(pipsArray);
    setSearchSubmitted(true);
    setBulkSearchInfo(bulkInfo);
    // Update search limit info
    if (searchLimitInfo) {
      setSearchLimitInfo(searchLimitInfo);
    }
    setSearchTerm(`Bulk Search: ${bulkInfo?.totalSearched || 0} records`);
    // Compute unmatched input names (by full name and/or national_id)
    if (bulkInfo && Array.isArray(bulkInfo.inputRecords)) {
      // Build a set of matched names and IDs from results
      const matchedNames = new Set(pipsArray.map(p => (p.full_name || '').toLowerCase().trim()));
      const matchedIds = new Set(pipsArray.map(p => (p.national_id || '').toLowerCase().trim()));
      // Find unmatched input records
      const unmatched = bulkInfo.inputRecords.filter(rec => {
        const name = [rec.first_name, rec.middle_name, rec.last_name].filter(Boolean).join(' ').toLowerCase().trim();
        const id = (rec.national_id || '').toLowerCase().trim();
        return !matchedNames.has(name) && (!id || !matchedIds.has(id));
      });
      setUnmatchedBulkInputs(unmatched);
    } else {
      setUnmatchedBulkInputs([]);
    }
    // Scroll to results
    setTimeout(() => {
      const resultsElement = document.getElementById('search-results');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Add these functions after the formatSearchHistoryDisplay function (around line 150)

  // Function to handle bulk history click
  const handleBulkHistoryClick = async (jsonQuery) => {
    const ok = await confirm({
      title: 'Bulk search query',
      message: 'This is a bulk search query. Do you want to view the details?',
      confirmLabel: 'View',
      cancelLabel: 'Cancel'
    });
    if (!ok) return;
    setFullQueryToShow(jsonQuery);
    setShowQueryModal(true);
  };
  

  // Function to format JSON for display
  const formatJsonForDisplay = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  return (
    <div className="page-container">
      {/* All stat cards live in ONE stats-section so the rows visually group together
          (header cards + screening-limit cards, no gap between them). */}
      {user && (
        <div className="stats-section">
          {/* Source card on the left; every other card is compact and sits in
              a grid beside it, so they take two short rows instead of three. */}
          <div className="stats-layout">
            <div className="stat-card stats-layout__lead stat-card--teal">
              <div className="stat-label">Source</div>
              <div className="stat-value stat-value--teal" style={{ fontSize: '1rem' }}>PIPs Database</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)', marginTop: '0.35rem' }}>
                Last updated: <strong>{formatLastUpdated(pipStats?.last_updated || pipsLastUpdated)}</strong>
              </div>
            </div>
            <div className="stats-layout__rest">
            <div className="stat-card stat-card--blue">
              <div className="stat-label">Domestic PIPs</div>
              <div className="stat-value stat-value--blue">
                {pipStats ? pipStats.total_domestic : '—'}
              </div>
            </div>
            <div className="stat-card stat-card--purple">
              <div className="stat-label">Foreign PIPs</div>
              <div className="stat-value stat-value--purple">
                {pipStats ? pipStats.total_foreign : '—'}
              </div>
            </div>
            <div className="stat-card stat-card--orange">
              <div className="stat-label">My searches</div>
              <div className="stat-value stat-value--orange">
                {pipStats ? pipStats.my_searches : '—'}
              </div>
            </div>

          {/* Screening limit cards (org users only) */}
          {searchLimitInfo && !isPrivilegedUser && (
            <>
                <div className="stat-card stat-card--teal">
                  <div className="stat-label">Package Name</div>
                  <div className="stat-value stat-value--teal">{searchLimitInfo.package}</div>
                </div>
                <div className="stat-card stat-card--blue">
                  <div className="stat-label">Single Screening Limit</div>
                  <div className="stat-value stat-value--blue">
                    {searchLimitInfo.screeningLimit === null ? 'Unlimited' : searchLimitInfo.screeningLimit}
                  </div>
                </div>
                <div className="stat-card stat-card--orange">
                  <div className="stat-label">Single Screenings Done</div>
                  <div className="stat-value stat-value--orange">{searchLimitInfo.screeningsDone}</div>
                </div>
                <div className="stat-card stat-card--teal">
                  <div className="stat-label">Single Screenings Left</div>
                  <div className={`stat-value ${searchLimitInfo.screeningsLeft === 0 && searchLimitInfo.screeningLimit !== null ? 'stat-value--red' : 'stat-value--green'}`}>
                    {searchLimitInfo.screeningsLeft === 'Unlimited' ? 'Unlimited' : searchLimitInfo.screeningsLeft}
                  </div>
                </div>
                <div className="stat-card stat-card--purple">
                  <div className="stat-label">Bulk Screening Limit</div>
                  <div className="stat-value stat-value--purple">
                    {(searchLimitInfo.batchScreeningLimit === null || searchLimitInfo.batchScreeningLimit === undefined)
                      ? 'Unlimited'
                      : searchLimitInfo.batchScreeningLimit}
                  </div>
                </div>
                <div className="stat-card stat-card--orange">
                  <div className="stat-label">Bulk Screenings Done</div>
                  <div className="stat-value stat-value--orange">
                    {searchLimitInfo.batchScreeningsDone || 0}
                  </div>
                </div>
                <div className="stat-card stat-card--teal">
                  <div className="stat-label">Bulk Screenings Left</div>
                  <div className={`stat-value ${
                    searchLimitInfo.batchScreeningsLeft === 0 && searchLimitInfo.batchScreeningLimit !== null
                      ? 'stat-value--red'
                      : searchLimitInfo.batchScreeningsLeft <= 5 && searchLimitInfo.batchScreeningLimit !== null
                      ? 'stat-value--orange'
                      : 'stat-value--green'
                  }`}>
                    {searchLimitInfo.batchScreeningLimit === 0
                      ? 0
                      : searchLimitInfo.batchScreeningsLeft === 'Unlimited'
                      ? 'Unlimited'
                      : searchLimitInfo.batchScreeningsLeft}
                  </div>
                </div>
                <div className="stat-card stat-card--brown">
                  <div className="stat-label">Total Searches</div>
                  <div className="stat-value stat-value--teal">
                    {(searchLimitInfo.screeningsDone || 0) + (searchLimitInfo.batchScreeningsDone || 0)}
                  </div>
                </div>
            </>
          )}
            </div>
          </div>

          {searchLimitInfo && !isPrivilegedUser && (
            <>
              {/* Warning footers shown inside the same stats-section */}
              {searchLimitInfo.screeningsLeft === 0 && searchLimitInfo.screeningLimit !== null && (
                <div className="stat-limit-warning">
                  <strong>Warning:</strong> This organisation has reached the maximum number of single searches allowed by their package.
                  Users will not be able to perform additional single searches until the package is upgraded.
                </div>
              )}
              {searchLimitInfo.batchScreeningsLeft === 0 && searchLimitInfo.batchScreeningLimit !== null && (
                <div className="stat-limit-warning">
                  <strong>Warning:</strong> This organisation has reached the maximum number of bulk searches allowed by their package.
                  Users will not be able to perform additional bulk searches until the package is upgraded.
                </div>
              )}
              {searchLimitInfo.batchScreeningsLeft > 0 &&
                searchLimitInfo.batchScreeningsLeft <= 5 &&
                searchLimitInfo.batchScreeningLimit !== null && (
                  <div className="stat-limit-caution">
                    <strong>Notice:</strong> This organisation has only {searchLimitInfo.batchScreeningsLeft} bulk search
                    {searchLimitInfo.batchScreeningsLeft === 1 ? '' : 'es'} remaining.
                  </div>
                )}
            </>
          )}
        </div>
      )}

      {/* Search Limit Alerts - Show at the top for better visibility */}
      {!isPrivilegedUser && searchLimitInfo && (
        <>
          {/* No searches left / bulk limits: shown once, in the stats panel above */}
          {/* Warning Alert - Very Low searches (1-2) */}
          {searchLimitInfo.screeningLimit !== null && searchLimitInfo.screeningsLeft > 0 && searchLimitInfo.screeningsLeft <= 2 && (
            <AlertBanner
              type="danger"
              message={`Critical: Only ${searchLimitInfo.screeningsLeft} search${searchLimitInfo.screeningsLeft === 1 ? '' : 'es'} remaining!`}
              onClose={() => {}}
            />
          )}
          
          {/* Warning Alert - Low searches (3-5) */}
          {searchLimitInfo.screeningLimit !== null && searchLimitInfo.screeningsLeft > 2 && searchLimitInfo.screeningsLeft <= 5 && (
            <AlertBanner
              type="warning"
              message={`Warning: Only ${searchLimitInfo.screeningsLeft} searches remaining in your package.`}
              onClose={() => {}}
            />
          )}
          
          {/* Info Alert - Moderate searches (6-10) */}
          {searchLimitInfo.screeningLimit !== null && searchLimitInfo.screeningsLeft > 5 && searchLimitInfo.screeningsLeft <= 10 && (
            <AlertBanner
              type="info"
              message={`You have ${searchLimitInfo.screeningsLeft} searches remaining.`}
              onClose={() => {}}
            />
          )}
          
        </>
      )}

      {/* Success Messages */}
      {successMessage && (
        <AlertBanner
          type="success"
          message={successMessage}
          onClose={() => setSuccessMessage('')}
        />
      )}

      {/* For privileged users, show a simpler search interface */}
      {isPrivilegedUser && (
        <div style={{ marginBottom: '2rem' }}>
          
          <div className="table-controls">
            <input
              type="text"
              placeholder="Search PIPs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              className="search-filter"
              ref={searchInputRef}
            />
            <button className="search-button" onClick={handleSearch} style={{ marginLeft: '10px', padding: '8px 20px' }}>
              Search
            </button>
            {searchTerm && (
              <button className="clear-search-button" onClick={clearSearch} style={{ marginLeft: '10px' }}>
                <FaTimes /> Clear
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Search Hero Section - Hide for privileged users or when search results are found */}
      {!isPrivilegedUser && (!searchSubmitted || allPips.length === 0) && (
        <div className="search-hero">
          <h1>Prominent Influential Persons (PIP) Search</h1>

          <div className="search-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Enter name or National ID number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className="search-input"
                ref={searchInputRef}
                disabled={searchLimitInfo?.screeningsLeft === 0 && searchLimitInfo?.screeningLimit !== null}
              />
              {searchTerm && (
                <FaTimes className="clear-icon" onClick={clearSearch} />
              )}
              <button 
                className="search-button" 
                onClick={handleSearch}
                disabled={searchLimitInfo?.screeningsLeft === 0 && searchLimitInfo?.screeningLimit !== null}
              >
                Search
              </button>
            </div>
            
            {/* Search Filters */}
            <div className="search-filters">
              <div 
                className={`filter-chip ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => applyFilter('all')}
              >
                All
              </div>
              <div 
                className={`filter-chip ${activeFilter === 'pip' ? 'active' : ''}`}
                onClick={() => applyFilter('pip')}
              >
                PIPs Only
              </div>
              <div 
                className={`filter-chip ${activeFilter === 'associate' ? 'active' : ''}`}
                onClick={() => applyFilter('associate')}
              >
                Associates Only
              </div>
              <div 
                className={`filter-chip ${activeFilter === 'local' ? 'active' : ''}`}
                onClick={() => applyFilter('local')}
              >
                Local Only
              </div>
              <div 
                className={`filter-chip ${activeFilter === 'foreign' ? 'active' : ''}`}
                onClick={() => applyFilter('foreign')}
              >
                Foreign Only
              </div>
              <div 
                className="filter-chip bulk-search-button"
                onClick={
                  searchLimitInfo?.batchScreeningsLeft === 0 && searchLimitInfo?.batchScreeningLimit !== null
                    ? undefined
                    : handleBulkSearch
                }
                style={{
                  opacity: searchLimitInfo?.batchScreeningsLeft === 0 && searchLimitInfo?.batchScreeningLimit !== null ? 0.5 : 1,
                  cursor: searchLimitInfo?.batchScreeningsLeft === 0 && searchLimitInfo?.batchScreeningLimit !== null ? 'not-allowed' : 'pointer',
                  pointerEvents: searchLimitInfo?.batchScreeningsLeft === 0 && searchLimitInfo?.batchScreeningLimit !== null ? 'none' : 'auto'
                }}
                title={searchLimitInfo?.batchScreeningsLeft === 0 && searchLimitInfo?.batchScreeningLimit !== null ? 'Batch screening limit reached' : 'Bulk Search'}
              >
                <FaUpload style={{ marginRight: '5px' }} /> Bulk Search
              </div>
            </div>
            
            {/* Search Tips */}
            <div className="search-tips">
              <FaInfoCircle className="info-icon" /> 
              <span>Tip: Search by full name or partial name. Use ID number for exact matches.</span>
            </div>
            
            {/* Recent searches suggestions */}
            {searchHistory.length > 0 && !searchTerm && (
  <div className="search-filters" style={{ marginTop: '1rem' }}>
    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>Recent searches:</span>
    {searchHistory.map((search, index) => {
  const formatted = formatSearchHistoryDisplay(search);
  return (
    <div 
      key={index} 
      className="filter-chip"
      data-bulk={formatted.isBulk ? "true" : "false"} // Add this line
      onClick={formatted.isBulk ? undefined : () => applySuggestion(formatted.fullQuery)}
          style={{ 
            backgroundColor: formatted.isBulk ? 'rgba(156, 39, 176, 0.3)' : 'rgba(255,255,255,0.4)',
            position: 'relative',
            paddingRight: formatted.isBulk ? '80px' : '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            border: formatted.isBulk ? '1px solid rgba(156, 39, 176, 0.5)' : 'none',
            cursor: formatted.isBulk ? 'default' : 'pointer',
            opacity: formatted.isBulk ? 0.9 : 1
          }}
          title={formatted.isBulk ? `Bulk search with ${formatted.recordCount} records - Click "View all" to see details` : formatted.fullQuery}
        >
          {formatted.isBulk && (
            <FaUpload style={{ fontSize: '0.8rem', opacity: 0.8 }} />
          )}
          <span style={{ fontSize: '0.85rem' }}>{formatted.display}</span>
          {formatted.isBulk && (
            <span
              style={{
                position: 'absolute',
                right: '8px',
                fontSize: '0.75rem',
                color: 'var(--info)',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: '500'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setFullQueryToShow(formatted.fullQuery);
                setShowQueryModal(true);
              }}
            >
              View all
            </span>
          )}
        </div>
      );
    })}
  </div>
)}
          </div>
        </div>
      )}
      

      {/* Results header — mirrors UN Sanctions layout exactly:
          Select page · Showing X of Y total PIPs · Search table · type chips · Export PDF */}
      {getPipsLength() > 0 && (
        <div
          className="search-results-info"
          id="search-results"
          style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}
        >
          <label
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}
            title="Select all rows on this page"
          >
            <input
              type="checkbox"
              checked={currentPips.length > 0 && currentPips.every(p => selectedPipIds.includes(p.id))}
              onChange={e => handleSelectAll(e.target.checked)}
            />
            Select page
          </label>
          <div className="search-count" style={{ flex: 1, fontSize: '0.8rem' }}>
            {bulkSearchInfo ? (
              <>Bulk Search Results: Found {getPipsLength()} PIPs from {bulkSearchInfo.totalSearched} searched records</>
            ) : (
              <>Showing {currentPips.length} of {getPipsLength()} {searchTerm ? `results for "${searchTerm}"` : 'total PIPs'}</>
            )}
            {selectedPipIds.length > 0 && (
              <span style={{ marginLeft: '0.75rem', fontSize: '0.85rem', color: 'var(--accent-dark,#00695c)', fontWeight: 600 }}>
                · {selectedPipIds.length} selected
                <button
                  type="button"
                  onClick={() => setSelectedPipIds([])}
                  style={{ marginLeft: '0.5rem', fontSize: '0.75rem', background: 'none', border: 'none', color: 'var(--text-secondary,#607d8b)', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  clear
                </button>
              </span>
            )}
            {!isPrivilegedUser && (searchTerm || bulkSearchInfo) && (
              <button
                className="clear-search-button"
                onClick={clearSearch}
                style={{ marginLeft: '0.75rem', fontSize: '0.75rem', padding: '0.25rem 0.55rem', whiteSpace: 'nowrap' }}
              >
                <FaTimes /> Clear Search
              </button>
            )}
          </div>
          {/* Table search field */}
          <input
            type="text"
            className="table-search-input"
            placeholder="Search table..."
            value={tableSearch}
            onChange={e => {
              setTableSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          {/* PIP type count chips — mirrors UN Sanctions ind/ent chips */}
          {(() => {
            const counts = getPipsArray().reduce((acc, p) => {
              const t = (p.pip_type || '').toLowerCase();
              if (t.includes('domestic') || t === 'local') acc.dom += 1;
              else if (t.includes('foreign')) acc.fgn += 1;
              else if (t.includes('international')) acc.intl += 1;
              return acc;
            }, { dom: 0, fgn: 0, intl: 0 });
            const onChip = (kind) => {
              setChipFilter(prev => prev === kind ? 'all' : kind);
              setCurrentPage(1);
            };
            return (
              <span className="un-results__split">
                {counts.dom > 0 && (
                  <button
                    type="button"
                    className={`un-results__split-btn${chipFilter === 'domestic' ? ' un-results__split-btn--active' : ''}`}
                    onClick={() => onChip('domestic')}
                    title={chipFilter === 'domestic' ? 'Show all (clear filter)' : 'Show only Domestic PIPs'}
                  >
                    <FaHome /> Domestic {counts.dom}
                  </button>
                )}
                {counts.fgn > 0 && (
                  <button
                    type="button"
                    className={`un-results__split-btn${chipFilter === 'foreign' ? ' un-results__split-btn--active' : ''}`}
                    onClick={() => onChip('foreign')}
                    title={chipFilter === 'foreign' ? 'Show all (clear filter)' : 'Show only Foreign PIPs'}
                  >
                    <FaGlobe /> Foreign {counts.fgn}
                  </button>
                )}
                {counts.intl > 0 && (
                  <button
                    type="button"
                    className={`un-results__split-btn${chipFilter === 'international' ? ' un-results__split-btn--active' : ''}`}
                    onClick={() => onChip('international')}
                    title={chipFilter === 'international' ? 'Show all (clear filter)' : 'Show only International PIPs'}
                  >
                    <FaUniversity /> International {counts.intl}
                  </button>
                )}
              </span>
            );
          })()}
          {/* Search Score slider — only for org users, inline next to Clear Search / Export. */}
          {!isPrivilegedUser
            && !!user?.organisation_id
            && (!bulkSearchInfo || (bulkSearchInfo && tableSearch.trim())) && (
            <div className="search-score-row" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <span className="search-score-label">Search Score:</span>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={minSimilarity}
                onChange={e => {
                  setMinSimilarity(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ width: 80 }}
              />
              <span className="search-score-value">{minSimilarity}</span>
            </div>
          )}
          <div className="button-group">
            <button className="export-button" onClick={exportPDF}>
              Export PDF{selectedPipIds.length > 0 ? ` (${selectedPipIds.length})` : ''}
            </button>
          </div>
        </div>
      )}

      {/* Loading and Error Messages */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading-spinner"></div>
          <div className="loading-text">Searching for results...</div>
        </div>
      )}
      
      {/* Error Messages - Enhanced styling */}
      {error && (
        <AlertBanner
          type="danger"
          message={error}
          onClose={() => setError(null)}
          actions={
            error.includes('administrator') ? 
            [{ label: 'Contact Support', onClick: () => window.location.href = 'mailto:support@company.com', primary: true }] : 
            undefined
          }
        />
      )}
      
      {/* No Results State */}
      {!loading && searchSubmitted && allPips.length === 0 && !error && (
        <div className="error-text">
          No results found for "<strong>{searchTerm}</strong>"
        </div>
      )}
      
      {/* PIP Card Grid — top pagination + cards + bottom pagination */}
      {!loading && allPips.length > 0 && (
        <>
          {/* Card grid */}
          <div className="pip-card-grid">
            {currentPips.map((pip, index) => {
              const hasAssociates = pip.associates && pip.associates.length > 0;
              const checked = selectedPipIds.includes(pip.id);
              const isAlt = index % 2 === 1;

              const institutionNames = Array.isArray(pip.institutions) && pip.institutions.length > 0
                ? pip.institutions.map(i => i.institution_name || '').filter(Boolean).join(', ')
                : 'N/A';

              return (
                <React.Fragment key={pip.id}>
                  {/* PIP Card — colored left border per type, matches UN Sanctions look */}
                  <div
                    className={
                      `pip-card pip-card--${
                        ((pip.pip_type || '').toLowerCase().includes('domestic') || (pip.pip_type || '').toLowerCase() === 'local')
                          ? 'domestic'
                          : (pip.pip_type || '').toLowerCase().includes('foreign')
                            ? 'foreign'
                            : (pip.pip_type || '').toLowerCase().includes('international')
                              ? 'international'
                              : 'unknown'
                      }${isAlt ? ' pip-card--alt' : ''}`
                    }
                  >

                    {/* Header: checkbox · name · type badge */}
                    <div className="pip-card__header">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => handleSelectOne(pip.id, e.target.checked)}
                        title="Select record"
                      />
                      <span className="pip-card__name" title={pip.full_name}>
                        {pip.full_name}
                      </span>
                      <span className={`pip-type-badge ${String(pip.pip_type || '').toLowerCase()}`}>
                        {pip.pip_type}
                      </span>
                    </div>

                    {/* Body: key fields side by side on one row */}
                    <div className="pip-card__body">
                      <div className="pip-card__field">
                        <span className="pip-card__label">Institution</span>
                        <span className="pip-card__value">{institutionNames}</span>
                      </div>
                      <div className="pip-card__field">
                        <span className="pip-card__label">Position</span>
                        <span className="pip-card__value">{pip.position || 'N/A'}</span>
                      </div>
                      <div className="pip-card__field">
                        <span className="pip-card__label">Country</span>
                        <span className="pip-card__value">{pip.country || 'Namibia'}</span>
                      </div>
                      <div className="pip-card__field">
                        <span className="pip-card__label">PIP Result Overview</span>
                        <span className="pip-card__value">{getPipResultOverview(pip)}</span>
                      </div>
                    </div>

                    {/* Associates */}
                    {hasAssociates ? (
                      <div className="pip-card__associates">
                        <div className="pip-card__associates-title">
                          Associates ({pip.associates.length})
                        </div>
                        {pip.associates.map((assoc, idx) => (
                          <div key={idx} className="pip-card__associate">
                            <span className="associate-indicator">↳</span>
                            <span>{getAssociateName(assoc)}</span>
                            {assoc.national_id && (
                              <span className="associate-id">(ID: {assoc.national_id})</span>
                            )}
                            {assoc.relationship_type && (
                              <span className="associate-relationship">· {assoc.relationship_type}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="pip-card__no-associates">
                        <span className="muted-italic">No associates captured yet</span>
                      </div>
                    )}

                    {/* Footer: actions + View details on the right (same as UN / UK Sanctions) */}
                    <div className="pip-card__footer">
                      {isPrivilegedUser && (
                        <div className="pip-card__actions">
                          <span
                            className={`status-indicator ${pip.is_active !== false ? 'active' : 'inactive'}`}
                            title={pip.is_active !== false ? 'Active' : 'Inactive'}
                          />
                          <ActionDropdown
                            pip={pip}
                            onEdit={() => handleEdit(pip, false)}
                            onViewDetails={() => handleEdit(pip, true)}
                            isPrivilegedUser={isPrivilegedUser}
                            onToggleStatus={(newStatus) => handleToggleStatus(pip.id, newStatus)}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        className="view-details-btn"
                        onClick={() => setDetailPip(pip)}
                      >
                        View details
                      </button>
                    </div>
                  </div>

                </React.Fragment>
              );
            })}
          </div>

          {/* Bottom pagination controls */}
          {renderPaginationControls()}
        </>
      )}

      {/* View Details — pop-up modal */}
      {detailPip && (() => {
        const pip = detailPip;
        return (
          <div className="modal-overlay record-detail-overlay" onClick={() => setDetailPip(null)}>
            <div
              className="modal-content record-detail-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pip-detail-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2 id="pip-detail-title">
                  {pip.full_name}
                  {pip.pip_type && (
                    <span className={`pip-type-badge ${String(pip.pip_type).toLowerCase()}`} style={{ marginLeft: '0.75rem', verticalAlign: 'middle' }}>
                      {pip.pip_type}
                    </span>
                  )}
                </h2>
                <button className="modal-close" onClick={() => setDetailPip(null)} aria-label="Close">×</button>
              </div>
              <div className="modal-body record-detail-modal__body">

              {/* Personal Information */}
              <div className="pip-detail-section">
                <h4 className="pip-detail-section__title">Personal Information</h4>
                <div className="pip-detail-grid">
                  <div className="pip-detail-field">
                    <span className="pip-detail-field__label">First Name</span>
                    <span className="pip-detail-field__value">{pip.first_name || 'N/A'}</span>
                  </div>
                  <div className="pip-detail-field">
                    <span className="pip-detail-field__label">Middle Name</span>
                    <span className="pip-detail-field__value">{pip.middle_name || '—'}</span>
                  </div>
                  <div className="pip-detail-field">
                    <span className="pip-detail-field__label">Last Name</span>
                    <span className="pip-detail-field__value">{pip.last_name || 'N/A'}</span>
                  </div>
                  <div className="pip-detail-field">
                    <span className="pip-detail-field__label">National ID</span>
                    <span className="pip-detail-field__value">{pip.national_id || 'N/A'}</span>
                  </div>
                  <div className="pip-detail-field">
                    <span className="pip-detail-field__label">PIP Type</span>
                    <span className="pip-detail-field__value">{pip.pip_type || 'N/A'}</span>
                  </div>
                  <div className="pip-detail-field">
                    <span className="pip-detail-field__label">Country</span>
                    <span className="pip-detail-field__value">{pip.country || 'Namibia'}</span>
                  </div>
                </div>
                <div className="pip-detail-field">
                  <span className="pip-detail-field__label">PIP Result Overview</span>
                  <div className="pip-detail-overview">{getPipResultOverview(pip)}</div>
                </div>
              </div>

              {/* Institutions */}
              {pip.institutions && pip.institutions.length > 0 && (
                <div className="pip-detail-section">
                  <h4 className="pip-detail-section__title">
                    Institutions ({pip.institutions.length})
                  </h4>
                  {pip.institutions.map((inst, i) => (
                    <div key={i} className="pip-detail-institution">
                      <div className="pip-detail-field">
                        <span className="pip-detail-field__label">Institution Name</span>
                        <span className="pip-detail-field__value">{inst.institution_name || 'N/A'}</span>
                      </div>
                      <div className="pip-detail-field">
                        <span className="pip-detail-field__label">Type</span>
                        <span className="pip-detail-field__value">{inst.institution_type || 'N/A'}</span>
                      </div>
                      <div className="pip-detail-field">
                        <span className="pip-detail-field__label">Position</span>
                        <span className="pip-detail-field__value">{inst.position || 'N/A'}</span>
                      </div>
                      <div className="pip-detail-field">
                        <span className="pip-detail-field__label">Start Date</span>
                        <span className="pip-detail-field__value">{formatDate(inst.start_date, 'N/A')}</span>
                      </div>
                      <div className="pip-detail-field">
                        <span className="pip-detail-field__label">End Date</span>
                        <span className="pip-detail-field__value">{formatDate(inst.end_date, 'Present')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Associates */}
              {pip.associates && pip.associates.length > 0 && (
                <div className="pip-detail-section">
                  <h4 className="pip-detail-section__title">
                    Associates ({pip.associates.length})
                  </h4>
                  {pip.associates.map((assoc, i) => (
                    <div key={i} className="pip-detail-associate">
                      <div className="pip-detail-field">
                        <span className="pip-detail-field__label">Full Name</span>
                        <span className="pip-detail-field__value">{getAssociateName(assoc) || 'N/A'}</span>
                      </div>
                      <div className="pip-detail-field">
                        <span className="pip-detail-field__label">Relationship</span>
                        <span className="pip-detail-field__value">{assoc.relationship_type || 'N/A'}</span>
                      </div>
                      {assoc.national_id && (
                        <div className="pip-detail-field">
                          <span className="pip-detail-field__label">National ID</span>
                          <span className="pip-detail-field__value">{assoc.national_id}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Foreign Details */}
              {pip.foreign && (
                <div className="pip-detail-section">
                  <h4 className="pip-detail-section__title">Foreign Details</h4>
                  <div className="pip-detail-grid">
                    <div className="pip-detail-field">
                      <span className="pip-detail-field__label">Country</span>
                      <span className="pip-detail-field__value">{pip.foreign.country || 'N/A'}</span>
                    </div>
                  </div>
                  {pip.foreign.additional_notes && (
                    <div className="pip-detail-field">
                      <span className="pip-detail-field__label">Additional Notes</span>
                      <div className="pip-detail-overview">{pip.foreign.additional_notes}</div>
                    </div>
                  )}
                </div>
              )}

              </div>
            </div>
          </div>
        );
      })()}

      {/* Unmatched Bulk Search Records Table: show below pagination and results table */}
      {bulkSearchInfo && bulkSearchInfo.unmatchedKeywords && bulkSearchInfo.unmatchedKeywords.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div className="unmatched-bulk-header">UNMATCHED BULK SEARCH RECORDS</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <table className="unmatched-bulk-table">
              <thead>
                <tr>
                  <th>Unmatched Bulk Keywords</th>
                </tr>
              </thead>
              <tbody>
                {bulkSearchInfo.unmatchedKeywords.map((keyword, idx) => (
                  <tr key={idx}>
                    <td>{keyword}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal */}
            {/* Edit Modal */}
      <EditPipModal
        showEditModal={showEditModal}
        closeEditModal={closeEditModal}
        editingPip={editingPip}
        editCurrentStep={editCurrentStep}
        editCompletedSteps={editCompletedSteps}
        handleEditStepClick={handleEditStepClick}
        successMessage={successMessage}
        savingStep={savingStep}
        handleSaveStep={handleSaveStep}
        // Form states
        editFirstName={editFirstName}
        setEditFirstName={setEditFirstName}
        editMiddleName={editMiddleName}
        setEditMiddleName={setEditMiddleName}
        editLastName={editLastName}
        setEditLastName={setEditLastName}
        editNationalId={editNationalId}
        editPipResultOverview={editPipResultOverview}
        editAssociates={editAssociates}
        editInstitutions={editInstitutions}
        editForeignDetails={editForeignDetails}
        editValidationErrors={editValidationErrors}
        setEditValidationErrors={setEditValidationErrors}
        setEditPipResultOverview={setEditPipResultOverview}
        // Handler functions
        handleEditNationalIdChange={handleEditNationalIdChange}
        handleEditAssociateChange={handleEditAssociateChange}
        addEditAssociate={addEditAssociate}
        removeEditAssociate={removeEditAssociate}
        handleEditInstitutionChange={handleEditInstitutionChange}
        addEditInstitution={addEditInstitution}
        removeEditInstitution={removeEditInstitution}
        handleEditCountryChange={handleEditCountryChange}
        selectEditCountry={selectEditCountry}
        editSuggestions={editSuggestions}
        viewMode={viewMode}
        user={user}
      />

      {/* Bulk Search Modal */}
      {showBulkSearchModal && (
        <div className="modal-overlay" onClick={closeBulkSearchModal}>
          <BulkSearchForm 
            closeModal={closeBulkSearchModal} 
            onSearchComplete={handleBulkSearchComplete}
          />
        </div>
      )}

      {/* Full Query Modal */}
      {showQueryModal && (
        <div className="modal-overlay" onClick={() => setShowQueryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Search Details</h3>
              <button className="modal-close" onClick={() => setShowQueryModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {(() => {
                try {
                  const records = JSON.parse(fullQueryToShow);
                  if (Array.isArray(records)) {
                    return (
                      <div>
                        <p style={{ marginBottom: '15px', fontWeight: '500' }}>
                          Total Records: {records.length}
                        </p>
                        <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                          <table className="pips-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>First Name</th>
                                <th>Middle Name</th>
                                <th>Last Name</th>
                                <th>National ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {records.map((record, idx) => (
                                <tr key={idx}>
                                  <td>{idx + 1}</td>
                                  <td>{record.first_name || '-'}</td>
                                  <td>{record.middle_name || '-'}</td>
                                  <td>{record.last_name || '-'}</td>
                                  <td>{record.national_id || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  }
                } catch (e) {
                  return (
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.9rem', maxHeight: '400px', overflow: 'auto' }}>
                      {formatJsonForDisplay(fullQueryToShow)}
                    </pre>
                  );
                }
              })()}

              <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  className="button-secondary"
                  onClick={async () => {
                    await navigator.clipboard.writeText(fullQueryToShow);
                    await notify({ title: 'Copied', message: 'Query copied to clipboard.' });
                  }}
                >
                  Copy JSON
                </button>
                <button 
                  className="button-secondary"
                  onClick={() => setShowQueryModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PIPs;
